import { useState, useRef, useEffect, useMemo } from 'react';
import { PushEnableCard } from '../components/PushNotificationSetup';
// F18 (packet 2026-09-24): per-condo "Visible en el directorio" opt-out card.
import DirectoryPrivacyCard from '../components/DirectoryPrivacyCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  User, Camera, ArrowLeft, Mail, Phone, MapPin, Home, 
  Lock, Edit2, Check, X, Shield, Bell, AlertTriangle, Trash2, LogOut,
  Building2, Plus, MapPin as MapPinIcon, Eye, EyeOff, Copy, Share2, KeyRound, UserPlus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, { authAPI } from '../services/api';
import { CondoOnboarding } from '../components/CondoLinking';
import { MapLocationPicker, type MapPickedAddress } from '../components/MapLocationPicker';
// i18n Stage 3 (2026-09-23): the Idioma select is bound to the i18n runtime.
// Changing it applies the locale app-wide immediately (optimistic), persists
// device-level 'v-lang', and still saves preferred_language via PATCH /auth/me.
import { setLanguage, getInitialLocale } from '../i18n';

// i18n Stage 3: labels for the 4 supported locales of preferred_language.
const LANGUAGE_LABELS: Record<string, string> = {
  'es-MX': 'Español (México)',
  'es-VE': 'Español (Venezuela)',
  'es-ES': 'Español (España)',
  'en': 'English'
};

// Demographics (2026-09-08): optional stats fields, saved via PATCH /auth/me.
const SEX_LABELS: Record<string, string> = {
  masculino: 'Masculino',
  femenino: 'Femenino',
  otro: 'Otro',
  prefiero_no_decir: 'Prefiero no decir'
};
const AGE_RANGE_LABELS: Record<string, string> = {
  '<18': 'Menos de 18',
  '18-24': '18–24',
  '25-34': '25–34',
  '35-44': '35–44',
  '45-54': '45–54',
  '55-64': '55–64',
  '65+': '65 o más',
  prefiero_no_decir: 'Prefiero no decir'
};

// P1-C (2026-09-08, spec ADDENDUM 2026-09-08): the "Preferencias por categoría"
// section (F102) was REMOVED from /edicion-perfil. User-scoped notification
// preferences remain server-managed via /notifications/preferences (P1-A
// contract: API is the single source of truth, no localStorage fallback).

export default function EdicionPerfil() {
  const navigate = useNavigate();
  const location = useLocation();
  // Highlight "Agregar propiedad" (Jaime 2026-10-07): when arriving from the
  // no-unit CTA on /resident-qr (?highlight=agregar-propiedad), pulse the
  // button for ~5s so the next step is obvious.
  const [highlightAgregar, setHighlightAgregar] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('highlight') === 'agregar-propiedad') {
      setHighlightAgregar(true);
      const t = setTimeout(() => setHighlightAgregar(false), 5000);
      return () => clearTimeout(t);
    }
  }, [location.search]);
  const { user, logout, refreshMe, memberships, activeCondoId, activeOwnedUnitId, setActiveOwnedUnit, setActiveCondo } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingEmailMessage, setPendingEmailMessage] = useState('');
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '', // No phone column in production users table
    address: user?.unit || user?.unitLabel ? `Unidad ${user.unit || user.unitLabel}` : 'Sin unidad asignada',
    avatar: null as string | null,
    bio: '',
    // P1-C (2026-09-08): ICE contact + directory visibility + language.
    iceContactName: '',
    iceContactPhone: '',
    directoryVisible: false,
    language: 'es',
    sex: '',
    ageRange: '',
    // (2026-09-08) Optional date of birth (date_of_birth); '' means not set, cleared sends null.
    dateOfBirth: '',
    notifications: true,
    twoFactor: false
  });
  const [tempProfile, setTempProfile] = useState(profile);
  // FEATURE (2026-09-01, Jaime): dedicated avatar endpoints (POST/DELETE /api/auth/me/avatar).
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  // (2026-09-08) Inline error banner for Información Personal saves (400 validation etc.).
  const [profileSaveError, setProfileSaveError] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState<'idle'|'enroll'|'backup'|'disable'>('idle');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorUrl, setTwoFactorUrl] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  // --- F101: Password change modal state ---
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);


  // Account deletion modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // --- Join Condo modal state ---
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showCondoRegistration, setShowCondoRegistration] = useState<string | null>(null);
  const [availableCondos, setAvailableCondos] = useState<any[]>([]);
  const [condoSearch, setCondoSearch] = useState('');
  const [selectedCondo, setSelectedCondo] = useState<any>(null);
  const [joinUnitLabel, setJoinUnitLabel] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);
  const [unitPickerOpen, setUnitPickerOpen] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [unitError, setUnitError] = useState('');
  const [claimingUnit, setClaimingUnit] = useState(false);
  type ClaimCandidate = { id: string; condoId: string; condoName: string; unitNumber?: string; name: string };
  const [claimCandidates, setClaimCandidates] = useState<ClaimCandidate[]>([]);
  const [claimCandidatesLoading, setClaimCandidatesLoading] = useState(false);
  type MyUnit = { id: string; unit_number?: string; household_name?: string; invite_code?: string; condo_id?: string | null; condo_name?: string | null; role?: string | null; is_owner?: boolean; isMembershipOnly?: boolean };
  // QA-T04-1: roles eligible for self-unlink (POST /api/units/:id/unlink, spec F128).
  const UNLINKABLE_ROLES = ['resident', 'residente', 'inquilino', 'empleado'];
  const roleLabel = (role?: string | null) => { const r = String(role || '').toLowerCase(); if (r === 'resident' || r === 'residente') return 'Residente'; if (r === 'inquilino') return 'Inquilino'; if (r === 'empleado') return 'Empleado'; return role ? String(role) : 'Miembro'; };
  type Transfer = { id: string; unit_id: string; to_email: string; from_user_id?: string; unit_number?: string; household_name?: string; condo_name?: string; direction?: 'incoming' | 'outgoing'; status?: string };
  const [outgoingTransfers, setOutgoingTransfers] = useState<Transfer[]>([]);
  const [myUnits, setMyUnits] = useState<MyUnit[]>([]);
  const [incomingTransfers, setIncomingTransfers] = useState<Transfer[]>([]);
  // P0 redesign: full standalone-unit create form (same fields as registration flow,
  // spec E7) + separate code inputs per flow (join / link / household share).
  type NewUnitForm = { householdName: string; street: string; number: string; neighborhood: string; locality: string; postal_code: string; city: string; country: string; latitude: string; longitude: string };
  const emptyNewUnit: NewUnitForm = { householdName: '', street: '', number: '', neighborhood: '', locality: '', postal_code: '', city: '', country: '', latitude: '', longitude: '' };
  const [showCreateUnit, setShowCreateUnit] = useState(() => {
    // 2026-09-24: deep-link from the "Crear mi hogar" nudge card. Reuses the
    // EXISTING create-property flow (POST /api/units, spec E7 contract) — no
    // new unit-creation path is introduced.
    try { return new URLSearchParams(window.location.search).get('createUnit') === '1'; } catch { return false; }
  });
  const [newUnit, setNewUnit] = useState<NewUnitForm>(emptyNewUnit);
  const [joinCode, setJoinCode] = useState('');
  const [linkCode, setLinkCode] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [propertyError, setPropertyError] = useState('');
  const [propertySuccess, setPropertySuccess] = useState('');
  const [propertyBusy, setPropertyBusy] = useState(false);
  const [copiedUnit, setCopiedUnit] = useState('');
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [editingUnitName, setEditingUnitName] = useState('');
  const [editingCondoId, setEditingCondoId] = useState<string | null>(null);
  const [editingCondoName, setEditingCondoName] = useState('');
  // Unit deletion (standalone owner) + unlink (Residente/Personal self-removal).
  const [deleteTarget, setDeleteTarget] = useState<MyUnit | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [leaveTarget, setLeaveTarget] = useState<MyUnit | null>(null);

  // Unit-centric ownership model: properties are the source of condo memberships.
  const loadProperties = async () => {
    try {
      const [unitsRes, transfersRes] = await Promise.all([api.get('/units/mine'), api.get('/units/transfers/pending')]);
      const unitsData = unitsRes.data?.data ?? unitsRes.data;
      const transfersData = transfersRes.data?.data ?? transfersRes.data;
      setMyUnits(unitsData?.units || []);
      setIncomingTransfers(transfersData?.transfers || []);
      setOutgoingTransfers((unitsData?.transfers || []).filter((t: Transfer) => t.status === 'pending'));
    } catch { setMyUnits([]); setIncomingTransfers([]); }
  };
  useEffect(() => { loadProperties(); }, []);
  // QA-T04-1: /api/units/mine only returns owner rows (owner_user_id=$1), so a
  // non-owner resident got [] and the Desvincularme button was unreachable.
  // Merge the resident membership rows already present in /api/auth/me
  // (memberships[].unitId/role) as display-only cards; owner-oriented controls
  // (rename/transfer/invite code/delete) are hidden for these rows.
  const mergedUnits = useMemo(() => {
    const ownedIds = new Set(myUnits.map(u => u.id));
    const membershipUnits = (memberships || [])
      .filter(m => m.unitId && m.role && UNLINKABLE_ROLES.includes(String(m.role).toLowerCase()) && !ownedIds.has(m.unitId))
      .map(m => ({ id: m.unitId as string, unit_number: m.unitNumber, household_name: m.unitLabel, condo_id: m.condoId, condo_name: m.condoName, role: m.role, is_owner: false, isMembershipOnly: true, invite_code: undefined }));
    return [...myUnits, ...membershipUnits];
  }, [myUnits, memberships]);

  // Offer ownership claims for every active membership, not only the selected condo.
  useEffect(() => {
    let cancelled = false;
    const loadClaimCandidates = async () => {
      if (!memberships?.length) { setClaimCandidates([]); return; }
      setClaimCandidatesLoading(true);
      try {
        // Ticket #296: memberships may legitimately carry condoId null/undefined
        // (standalone units; backend 2026-09-07 ruling). Interpolating null into the
        // URL fires GET /condos/null/units/available -> 400 'Valid condo ID required.',
        // beaconed as CLIENT_ERROR by the api.ts interceptor. Skip those memberships:
        // no condo means no condo-scoped claimable units.
        const claimable = memberships.filter((m) => m.condoId != null && String(m.condoId) !== 'null' && String(m.condoId) !== 'undefined' && String(m.condoId) !== '');
        const results = await Promise.all(claimable.map(async (membership) => {
          try {
            const response = await api.get(`/condos/${membership.condoId}/units/available`);
            const data = response.data?.data ?? response.data;
            return (data?.units || []).filter((unit: any) => !unit.mine).map((unit: any) => ({
              id: unit.id, condoId: membership.condoId, condoName: membership.condoName || 'Condominio', unitNumber: unit.unit_number, name: ''
            }));
          } catch { return []; }
        }));
        if (!cancelled) setClaimCandidates(results.flat());
      } finally { if (!cancelled) setClaimCandidatesLoading(false); }
    };
    loadClaimCandidates();
    return () => { cancelled = true; };
  }, [memberships]);

  // Fetch real profile data from API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get('/auth/me');
        // API returns { success: true, data: { user: {...}, roles: [...] } }
        const u = res.data?.data?.user || res.data?.user || res.data;
        if (u) {
          const profileData = {
            name: u.name || u.fullName || '',
            email: u.email || '',
            phone: u.phone || '',
            address: u.unit || u.unitLabel ? `Unidad ${u.unit || u.unitLabel}` : 'Sin unidad asignada',
            avatar: u.avatarUrl ?? u.avatar_url ?? null,
            bio: u.bio || '',
            iceContactName: u.ice_contact_name || '',
            iceContactPhone: u.ice_contact_phone || '',
            sex: u.sex || '',
            ageRange: u.age_range || '',
            dateOfBirth: u.date_of_birth ? String(u.date_of_birth).slice(0, 10) : '',
            directoryVisible: u.directory_visible === true || u.directory_visible === 1,
            language: u.preferredLanguage || u.preferred_language || 'es-MX',
            notifications: true,
            twoFactor: false
          };
          setProfile(profileData);
          setTempProfile(profileData);
        }
      } catch (err) {
        // Fallback to auth context user
        if (user && typeof user === 'object') {
          const profileData = {
            name: typeof user.name === 'string' ? user.name : '',
            email: typeof user.email === 'string' ? user.email : '',
            phone: '',
            address: user.unit || user.unitLabel ? `Unidad ${user.unit || user.unitLabel}` : 'Sin unidad asignada',
            avatar: null,
            bio: '',
            iceContactName: '',
            iceContactPhone: '',
            directoryVisible: false,
            language: 'es',
            sex: '',
            ageRange: '',
            dateOfBirth: '',
            notifications: true,
            twoFactor: false
          };
          setProfile(profileData);
          setTempProfile(profileData);
        }
      }
    };
    fetchProfile();
  }, [user]);

  // Fetch available condos when join modal opens or search changes
  useEffect(() => {
    if (!showJoinModal) return;
    const fetchCondos = async () => {
      try {
        // Use the public search endpoint (no auth required)
        const res = await api.get('/condos/search', { params: condoSearch ? { q: condoSearch } : {} });
        const data = res.data?.data ?? res.data;
        setAvailableCondos(Array.isArray(data) ? data : data?.condos || []);
      } catch (err) {
        // Fallback to /condos/available
        try {
          const res2 = await api.get('/condos/available', { params: condoSearch ? { search: condoSearch } : {} });
          setAvailableCondos(res2.data?.data?.condos || []);
        } catch {
          setAvailableCondos([]);
        }
      }
    };
    const timer = setTimeout(fetchCondos, 200);
    return () => clearTimeout(timer);
  }, [showJoinModal, condoSearch]);

  const start2FA = async () => { setTwoFactorError(''); setTwoFactorLoading(true); try { const r = await authAPI.enroll2FA(); const d = r.data?.data || r.data; setTwoFactorSecret(d.secret); setTwoFactorUrl(d.otpauthUrl); setTwoFactorStep('enroll'); } catch (e: any) { setTwoFactorError(e.response?.data?.error || 'No se pudo iniciar.'); } finally { setTwoFactorLoading(false); } };
  const confirm2FA = async () => { if (twoFactorCode.length !== 6) { setTwoFactorError('Escribe un código de 6 dígitos.'); return; } setTwoFactorLoading(true); try { const r = await authAPI.enable2FA(twoFactorCode); setBackupCodes(r.data?.data?.backupCodes || r.data?.backupCodes || []); setTwoFactorEnabled(true); setTwoFactorStep('backup'); setTwoFactorCode(''); } catch (e: any) { setTwoFactorError(e.response?.data?.error || 'Código incorrecto.'); } finally { setTwoFactorLoading(false); } };
  const disable2FA = async () => { setTwoFactorLoading(true); try { await authAPI.disable2FA(twoFactorCode); setTwoFactorEnabled(false); setTwoFactorStep('idle'); setTwoFactorCode(''); } catch (e: any) { setTwoFactorError(e.response?.data?.error || 'No se pudo desactivar.'); } finally { setTwoFactorLoading(false); } };

  // FEATURE (2026-09-01, Jaime): avatar upload via POST /api/auth/me/avatar
  // (base64 image body, <=5MB, magic-byte validated server-side).
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || avatarBusy) return;
    setAvatarError('');
    if (!file.type.startsWith('image/')) {
      setAvatarError('El archivo debe ser una imagen (JPEG, PNG, GIF o WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('La imagen supera el límite de 5MB.');
      return;
    }
    setAvatarBusy(true);
    try {
      // FIX (2026-09-01, Jaime): phone photos (HEIC/large) were rejected by the
      // backend magic-byte/size checks. Re-encode on-device: decode to a
      // bitmap, downscale to max 1024px, export as JPEG — always upload a
      // backend-compatible payload regardless of source format.
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          try {
            const maxDim = 1024;
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('No se pudo procesar la imagen')); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          } finally {
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Formato de imagen no soportado por este navegador. Intenta con una foto JPEG o PNG.')); };
        img.src = url;
      });
      const res = await api.post('/auth/me/avatar', { file: dataUrl, fileName: file.name.replace(/\.[^.]+$/, '') + '.jpg', mimeType: 'image/jpeg' });
      const url = res.data?.data?.avatarUrl ?? null;
      setProfile((prev) => ({ ...prev, avatar: url }));
      setTempProfile((prev) => ({ ...prev, avatar: url }));
      await refreshMe();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'No se pudo subir la foto';
      setAvatarError(msg);
    } finally {
      setAvatarBusy(false);
    }
  };

  // FEATURE (2026-09-01, Jaime): clear avatar via DELETE /api/auth/me/avatar.
  const handleRemoveAvatar = async () => {
    if (avatarBusy) return;
    setAvatarError('');
    setAvatarBusy(true);
    try {
      await api.delete('/auth/me/avatar');
      setProfile((prev) => ({ ...prev, avatar: null }));
      setTempProfile((prev) => ({ ...prev, avatar: null }));
      await refreshMe();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'No se pudo quitar la foto';
      setAvatarError(msg);
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleSave = async () => {
    try {
      // Map profile fields to API fields
      const updates: Record<string, any> = {};
      if (tempProfile.name !== profile.name) updates.name = tempProfile.name;
      // Identity batch 2 (item C, 2026-09-16): email changes now go through
      // POST /auth/request-email-change (verified-contacts policy) instead of
      // PATCH /auth/me — the new address must be confirmed via the emailed
      // link before the swap. The old email is never deleted.
      let emailChangeRequested = false;
      if (tempProfile.email && profile.email !== undefined && tempProfile.email.trim().toLowerCase() !== String(profile.email).trim().toLowerCase()) {
        const res = await api.post('/auth/request-email-change', { new_email: tempProfile.email.trim().toLowerCase() });
        if (res.data?.success) {
          emailChangeRequested = true;
          setPendingEmailMessage('Revisa tu nuevo correo: te enviamos un enlace para confirmar el cambio. Tu correo actual sigue activo.');
        }
      }
      if (tempProfile.phone !== profile.phone) updates.phone = tempProfile.phone;
      if (tempProfile.bio !== profile.bio) updates.bio = tempProfile.bio;
      // P1-C (2026-09-08): ICE contact, directory visibility, language.
      // NOTE: ice_contact_name / ice_contact_phone / directory_visible are being
      // whitelisted on PATCH /auth/me by the backend (increment-1 addendum); the
      // current allowlist does not include them yet, so PATCH may ignore them.
      if (tempProfile.iceContactName !== profile.iceContactName) updates.ice_contact_name = tempProfile.iceContactName;
      if (tempProfile.iceContactPhone !== profile.iceContactPhone) updates.ice_contact_phone = tempProfile.iceContactPhone;
      // Demographics (2026-09-08): optional sex / age_range; empty value sends null so the field can be cleared.
      if (tempProfile.sex !== profile.sex) updates.sex = tempProfile.sex || null;
      if (tempProfile.ageRange !== profile.ageRange) updates.age_range = tempProfile.ageRange || null;
      // (2026-09-08) Fecha de nacimiento (date_of_birth): optional; empty value sends null so the field can be cleared.
      if (tempProfile.dateOfBirth !== profile.dateOfBirth) updates.date_of_birth = tempProfile.dateOfBirth || null;
      if (tempProfile.directoryVisible !== profile.directoryVisible) updates.directory_visible = tempProfile.directoryVisible;
      if (tempProfile.language !== profile.language) updates.preferredLanguage = tempProfile.language;
      // P1-C: Unidad is no longer an editable field — membership editing is the
      // admin/invitation flow only, so the legacy unitLabel update was removed.

      if (Object.keys(updates).length > 0) {
        const response = await api.patch('/auth/me', updates);
        if ((response.data?.data?.pendingEmail || response.data?.pendingEmail) && !emailChangeRequested) setPendingEmailMessage('Te enviamos un enlace de verificación a tu nuevo correo');
        await refreshMe();
      }
      if (emailChangeRequested) {
        // Do NOT apply the new email locally — it is only active after the
        // user confirms the link sent to the new address.
        setIsEditing(false);
        alert('✅ Cambio de correo solicitado. Revisa tu nuevo correo para confirmar.');
        return;
      }
      setProfile(tempProfile);
      setIsEditing(false);
      alert('✅ Perfil actualizado correctamente');
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Error al guardar el perfil';
      // (2026-09-08) Surface save errors (e.g. 400 validation for date_of_birth) in the inline banner.
      setProfileSaveError(msg);
      // i18n Stage 3: an unsaved language switch was not persisted — restore
      // the pre-session device locale so the UI matches what the server has.
      if (tempProfile.language !== profile.language) await restorePreSessionLocale();
    }
  };

  // --- i18n Stage 3 (2026-09-23): live language switching -------------------
  // The Idioma select is optimistic: it flips the i18n runtime and persists
  // device-level 'v-lang' immediately; preferred_language still rides the next
  // "Guardar" (PATCH /auth/me). On cancel or save failure the pre-session
  // device locale is restored exactly — including removing 'v-lang' when the
  // browser default was in effect before this session.
  const vLangAtMountRef = useRef<string | null | undefined>(undefined);
  if (vLangAtMountRef.current === undefined) {
    try { vLangAtMountRef.current = localStorage.getItem('v-lang'); } catch { vLangAtMountRef.current = null; }
  }

  /** Restore the app language to the pre-session device locale (v-lang as at mount). */
  const restorePreSessionLocale = async () => {
    try {
      const mounted = vLangAtMountRef.current ?? null;
      if (mounted === null) {
        try { localStorage.removeItem('v-lang'); } catch { /* storage optional */ }
      } else {
        try { localStorage.setItem('v-lang', mounted); } catch { /* storage optional */ }
      }
      await setLanguage(getInitialLocale());
    } catch { /* keep current locale */ }
  };

  /** Optimistically apply a select value (es-MX | es-VE | es-ES | en; legacy 'es' -> es-MX). */
  const handleLanguageChange = async (next: string) => {
    const locale = ['es-MX', 'es-VE', 'es-ES', 'en'].includes(next)
      ? next
      : (next === 'es' ? 'es-MX' : null);
    if (!locale) return;
    try {
      const resolved = await setLanguage(locale);
      try { localStorage.setItem('v-lang', resolved); } catch { /* storage optional */ }
    } catch (err) {
      // Pack load failed: keep the previous locale.
      console.error('[i18n] language switch failed', err);
      await restorePreSessionLocale();
    }
  };

  const handleCancel = () => {
    // i18n Stage 3: an unsaved language switch must not stick after Cancel.
    if (tempProfile.language !== profile.language) void restorePreSessionLocale();
    setTempProfile(profile);
    setIsEditing(false);
  };

  // --- F101: Password change handlers ---
  const handleOpenPasswordModal = () => {
    setPwCurrent(''); setPwNew(''); setPwConfirm('');
    setPwError(''); setPwSuccess('');
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPwCurrent(''); setPwNew(''); setPwConfirm('');
    setPwError(''); setPwSuccess(''); setPwLoading(false);
  };

  const handleChangePassword = async () => {
    setPwError(''); setPwSuccess('');
    if (!pwCurrent || !pwNew || !pwConfirm) {
      setPwError('Todos los campos son obligatorios');
      return;
    }
    if (pwNew.length < 8) {
      setPwError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError('Las contraseñas no coinciden');
      return;
    }
    if (pwCurrent === pwNew) {
      setPwError('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setPwLoading(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: pwCurrent,
        newPassword: pwNew,
      });
      setPwSuccess('Contraseña cambiada correctamente');
      setTimeout(() => closePasswordModal(), 1800);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al cambiar la contraseña';
      setPwError(msg);
    } finally {
      setPwLoading(false);
    }
  };

  const loadAvailableUnits = async () => {
    if (!activeCondoId) return;
    setUnitLoading(true); setUnitError(''); setUnitPickerOpen(true);
    try {
      const response = await api.get(`/condos/${activeCondoId}/units/available`);
      const data = response.data?.data ?? response.data;
      setAvailableUnits(data?.units || []);
    } catch { setUnitError('No se pudieron cargar las unidades. Intenta de nuevo.'); }
    finally { setUnitLoading(false); }
  };

  const claimUnit = async (unitId: string) => {
    if (!activeCondoId) return;
    setClaimingUnit(true); setUnitError('');
    try {
      await api.post(`/condos/${activeCondoId}/claim-unit`, { unitId, claimOwnership: true });
      await refreshMe();
      setUnitPickerOpen(false);
    } catch (err: any) {
      if (err?.response?.status === 409) { setUnitError('Esta unidad ya tiene propietario. Actualizamos la lista.'); await loadAvailableUnits(); }
      else setUnitError(err?.response?.data?.error || 'No se pudo reclamar la unidad.');
    } finally { setClaimingUnit(false); }
  };

  const claimMembershipUnit = async (candidate: ClaimCandidate) => {
    if (!candidate.name.trim()) { setPropertyError('Ponle un nombre a la unidad antes de reclamarla.'); return; }
    setClaimingUnit(true); setPropertyError('');
    try {
      await api.post(`/condos/${candidate.condoId}/claim-unit`, { unitId: candidate.id, claimOwnership: true });
      await api.patch(`/units/${candidate.id}`, { householdName: candidate.name.trim() });
      await refreshMe(); await loadProperties();
      setClaimCandidates(current => current.filter(item => item.id !== candidate.id));
    } catch (err: any) {
      setPropertyError(err?.response?.status === 409 ? 'Esta unidad ya tiene propietario.' : err?.response?.data?.error || 'No se pudo reclamar la unidad.');
    } finally { setClaimingUnit(false); }
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) {
      setDeleteError('Debes ingresar tu contraseña');
      return;
    }
    if (!deleteConfirmed) {
      setDeleteError('Debes confirmar que entiendes que esta acción es permanente');
      return;
    }

    setDeleting(true);
    try {
      // Use the axios api instance — its interceptor automatically attaches
      // the Authorization: Bearer <token> header from localStorage.
      await api.delete('/auth/account', { data: { password: deletePassword } });

      localStorage.removeItem('vecindus_auth');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('vecindus_lens');
      
      logout();
      navigate('/login', { replace: true });
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setDeleteError('Tu sesión ha expirado. Por favor inicia sesión de nuevo e intenta nuevamente.');
      } else {
        setDeleteError(err?.response?.data?.error || err?.response?.data?.message || 'Error de conexión. Intenta de nuevo.');
      }
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteConfirmed(false);
    setDeleteError('');
    setDeleting(false);
  };

  // P0 redesign: distinct, correct messages per failure mode — never one generic
  // "código no válido" for every error. Backend contract: unit/routes.js /join
  // (404 unknown code, 400 standalone-not-linked / inactive condo, 409 claimed,
  // 403 not-owner) and standalone-unit.js (STANDALONE_ADDRESS_REQUIRED,
  // UNIT_NUMBER_TOO_LONG).
  const backendError = (err: any) => err?.response?.data?.error || err?.response?.data?.message || '';
  const createUnitErrorMessage = (err: any) => {
    const code = err?.response?.data?.code;
    const status = err?.response?.status;
    if (code === 'STANDALONE_ADDRESS_REQUIRED') return 'Falta la dirección completa. País, calle, número, colonia, localidad y código postal son obligatorios para crear una propiedad independiente.';
    if (code === 'UNIT_NUMBER_TOO_LONG') return 'El nombre/número de la unidad no puede exceder 20 caracteres.';
    if (code === 'INVALID_COORDINATES') return 'Coordenadas inválidas: la latitud va de -90 a 90 y la longitud de -180 a 180. Corrige o borra las coordenadas e intenta de nuevo.';
    if (status === 400) return backendError(err) || 'Revisa los datos de la dirección e intenta de nuevo.';
    return backendError(err) || 'No se pudo crear la propiedad. Intenta de nuevo.';
  };
  const joinCodeErrorMessage = (err: any) => {
    const status = err?.response?.status;
    const backend = backendError(err);
    if (status === 404) return 'Ese código de unidad no existe. Pídeselo de nuevo al propietario o a la administración y verifica que esté completo.';
    if (status === 400 && backend.includes('no está vinculada a un condominio')) return 'Ese código pertenece a una propiedad independiente (fuera de un condominio). Si esa propiedad es tuya, vínculala desde "Mis propiedades" con la opción "Vincular a condominio"; si quieres habitarla, pide al propietario que te comparta su código de unidad.';
    if (status === 400 && backend.includes('no está activo')) return 'El condominio de esa unidad no está activo. Contacta a la administración del condominio.';
    if (status === 409) return backend || 'Esta unidad ya fue reclamada por otro propietario. Contacta a tu administración.';
    return backend || 'No se pudo completar la operación. Intenta de nuevo.';
  };
  const linkCodeErrorMessage = (err: any) => {
    const status = err?.response?.status;
    const backend = backendError(err);
    if (status === 404) return 'Ese código de dirección no existe en ningún condominio. Verifica con la administración que sea el código de tu dirección.';
    if (status === 400 && backend.includes('no está vinculada a un condominio')) return 'Ese código corresponde a una propiedad independiente, no a una dirección de condominio. Para un condominio, pide el código de la dirección en el condominio a la administración.';
    if (status === 400 && backend.includes('ya está vinculada')) return 'Tu propiedad ya está vinculada a un condominio.';
    if (status === 400 && backend.includes('no está activo')) return 'Ese condominio no está activo. Contacta a su administración.';
    if (status === 403) return backend || 'Solo puedes vincular unidades de tu propiedad.';
    if (status === 409) return backend || 'Esa dirección ya fue reclamada. Contacta a la administración del condominio.';
    return backend || 'No se pudo completar la operación. Intenta de nuevo.';
  };
  // Flow A: join a unit inside a condo using the condo's unit code.
  const joinWithUnitCode = async () => {
    setPropertyError(''); setPropertySuccess('');
    if (!joinCode.trim()) { setPropertyError('Escribe el código de la unidad del condominio.'); return; }
    setPropertyBusy(true);
    try { await api.post('/units/join', { inviteCode: joinCode.trim() }); setJoinCode(''); await refreshMe(); await loadProperties(); setPropertySuccess('Te uniste a la unidad correctamente.'); }
    catch (err) { setPropertyError(joinCodeErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  // Flow B: create a standalone (unlinked) property. Same address contract as the
  // registration flow (spec E7) — reuse of the backend unit/standalone-unit.js
  // validation via POST /api/units; do not relax requirements here.
  const createProperty = async () => {
    setPropertyError(''); setPropertySuccess('');
    const required: Array<[keyof NewUnitForm, string]> = [
      ['country', 'el país'], ['street', 'la calle'], ['number', 'el número'],
      ['neighborhood', 'la colonia'], ['locality', 'la localidad'], ['postal_code', 'el código postal'],
    ];
    const missing = required.find(([key]) => !String(newUnit[key] ?? '').trim());
    if (missing) { setPropertyError(`Ingresa ${missing[1]} para crear tu propiedad.`); return; }
    // Optional coordinates (POST /api/units contract): only sent when both are
    // valid numbers; empty means omit — never send null/empty strings.
    // Coordinates now come exclusively from the embedded map picker marker.
    const latTrim = newUnit.latitude.trim();
    const lngTrim = newUnit.longitude.trim();
    const nlat = Number(latTrim);
    const nlng = Number(lngTrim);
    if ((latTrim === '') !== (lngTrim === '') || (latTrim !== '' && (!Number.isFinite(nlat) || !Number.isFinite(nlng)))) {
      setPropertyError('Coordenadas inválidas: coloca el marcador en el mapa o borra la ubicación.');
      return;
    }
    const coordsPayload = latTrim !== '' && lngTrim !== ''
      ? { latitude: nlat, longitude: nlng }
      : {};
    setPropertyBusy(true);
    try {
      const addressData = { street: newUnit.street.trim(), number: newUnit.number.trim(), neighborhood: newUnit.neighborhood.trim(), locality: newUnit.locality.trim(), postal_code: newUnit.postal_code.trim() };
      await api.post('/units', {
        householdName: newUnit.householdName.trim() || null,
        addressData,
        country: newUnit.country.trim(),
        city: newUnit.city.trim() || null,
        ...coordsPayload,
      });
      setNewUnit(emptyNewUnit); setShowCreateUnit(false);
      // FIX 2026-09-03 (Jaime bug): the context switcher ("Seleccionar contexto")
      // is driven by AuthContext.ownedUnits, which only updates on refreshMe().
      // Without this, a newly created property did not appear there until a full
      // page refresh. Same pattern as the join/link/claim flows above.
      await refreshMe();
      await loadProperties();
      setPropertySuccess('Propiedad creada correctamente.');
    }
    catch (err) { setPropertyError(createUnitErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  const updateNewUnit = (key: keyof NewUnitForm) => (e: React.ChangeEvent<HTMLInputElement>) => setNewUnit((prev) => ({ ...prev, [key]: e.target.value }));
  // Flow C: link MY standalone unit to a condo address using the condo's
  // address code — separate input/state from the join flow.
  const linkProperty = async () => {
    setPropertyError(''); setPropertySuccess('');
    if (!selectedUnitId || !linkCode.trim()) { setPropertyError('Escribe el código de tu dirección en el condominio.'); return; }
    setPropertyBusy(true);
    try {
      const response = await api.post('/units/join', { inviteCode: linkCode.trim(), linkUnitId: selectedUnitId });
      const condoName = response.data?.data?.condo?.name || response.data?.condo?.name || response.data?.data?.condo_name || 'el condominio';
      setLinkCode(''); setSelectedUnitId(null); await refreshMe(); await loadProperties();
      setPropertySuccess(`Tu propiedad ahora está vinculada a ${condoName}. Eres el propietario registrado.`);
    }
    catch (err) { setPropertyError(linkCodeErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  const transferErrorMessage = (err: any) => backendError(err) || 'No se pudo completar la operación. Intenta de nuevo.';
  const transferProperty = async (unitId: string) => {
    setPropertyError('');
    if (!transferEmail.trim()) { setPropertyError('Escribe el correo del nuevo propietario.'); return; }
    setPropertyBusy(true);
    try { const response = await api.post(`/units/${unitId}/transfer`, { email: transferEmail.trim() }); const requestId = response.data?.data?.requestId; if (requestId) setOutgoingTransfers(prev => [...prev, { id: requestId, unit_id: unitId, to_email: transferEmail.trim(), direction: 'outgoing', status: 'pending' }]); setTransferEmail(''); await loadProperties(); }
    catch (err) { setPropertyError(transferErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  const respondTransfer = async (id: string, action: 'accept' | 'decline') => {
    setPropertyBusy(true); setPropertyError('');
    try { await api.post(`/units/transfer/${id}/${action}`); await refreshMe(); await loadProperties(); }
    catch (err) { setPropertyError(transferErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  const cancelTransfer = async (id: string) => {
    if (!window.confirm('¿Cancelar esta transferencia pendiente?')) return;
    setPropertyBusy(true); setPropertyError('');
    try { await api.delete(`/units/transfers/${id}`); await loadProperties(); setPropertySuccess('Transferencia cancelada.'); }
    catch (err) { setPropertyError(transferErrorMessage(err)); } finally { setPropertyBusy(false); }
  };
  const copyCode = async (unit: MyUnit) => {
    if (!unit.invite_code) return;
    await navigator.clipboard?.writeText(unit.invite_code); setCopiedUnit(unit.id); setTimeout(() => setCopiedUnit(''), 1800);
  };
  // Graceful context clearing after a unit is deleted/left (Jaime ruling 4):
  // reuse AuthContext.setActiveCondo(null) — the existing context-clear logic —
  // so we never hand-roll stale-context handling. Personal state => the
  // add/link-condo CTA is shown by the home lens as usual.
  const clearContextForUnit = async (unit: MyUnit) => {
    if (activeOwnedUnitId === unit.id) setActiveOwnedUnit(undefined);
    if (unit.condo_id && activeCondoId === unit.condo_id) { await setActiveCondo(null); }
    else if (!unit.condo_id && activeOwnedUnitId === unit.id) { await setActiveCondo(null); }
    await refreshMe();
    await loadProperties();
  };
  const deleteProperty = async () => {
    if (!deleteTarget) return;
    setPropertyError(''); setPropertySuccess('');
    if (deleteConfirmText.trim().toLowerCase() !== 'eliminar') { setPropertyError('Escribe "eliminar" para confirmar.'); return; }
    setPropertyBusy(true);
    try {
      // Contract (unit_delete_backend): DELETE /api/units/:id body { confirm: 'eliminar' } — owner-only, standalone units.
      await api.delete(`/units/${deleteTarget.id}`, { data: { confirm: 'eliminar' } });
      const deleted = deleteTarget; setDeleteTarget(null); setDeleteConfirmText('');
      await clearContextForUnit(deleted);
      setPropertySuccess('Propiedad eliminada. La suscripción Home+ activa fue cancelada y los residentes fueron desvinculados.');
    }
    catch (err: any) { setPropertyError(backendError(err) || 'No se pudo eliminar la propiedad. Intenta de nuevo.'); }
    finally { setPropertyBusy(false); }
  };
  const leaveUnit = async () => {
    if (!leaveTarget) return;
    setPropertyError(''); setPropertySuccess('');
    setPropertyBusy(true);
    try {
      // Contract (unit_delete_backend): self-removal (unlink) for Residente/Personal.
      await api.post(`/units/${leaveTarget.id}/unlink`);
      const left = leaveTarget; setLeaveTarget(null);
      await clearContextForUnit(left);
      setPropertySuccess('Te desvinculaste de la unidad correctamente.');
    }
    catch (err: any) { setPropertyError(backendError(err) || 'No se pudo desvincular de la unidad. Intenta de nuevo.'); }
    finally { setPropertyBusy(false); }
  };
  const renameUnit = async (unitId: string) => {
    const name = editingUnitName.trim();
    if (!name) return;
    setPropertyBusy(true); setPropertyError('');
    try { await api.patch(`/units/${unitId}`, { householdName: name }); setEditingUnitId(null); await loadProperties(); await refreshMe(); }
    catch (err: any) { setPropertyError(err?.response?.data?.error || 'No se pudo renombrar la propiedad.'); }
    finally { setPropertyBusy(false); }
  };
  const renameCondo = async (condoId: string) => {
    const name = editingCondoName.trim();
    if (!name) return;
    setPropertyBusy(true); setPropertyError('');
    try { await api.patch(`/condos/${condoId}`, { name }); setEditingCondoId(null); await refreshMe(); }
    catch (err: any) { setPropertyError(err?.response?.data?.error || 'No se pudo renombrar el condominio.'); }
    finally { setPropertyBusy(false); }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-40">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Mi Perfil</h1>
          </div>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-[var(--v-teal-400)] text-white font-bold rounded-xl hover:bg-[var(--v-teal-500)] flex items-center gap-2 transition-colors">
              <Edit2 className="w-4 h-4" /> Editar
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleCancel} className="p-2 text-[var(--v-danger)] hover:bg-[var(--v-danger-bg)] rounded-xl"><X className="w-5 h-5" /></button>
              <button onClick={handleSave} className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-xl"><Check className="w-5 h-5" /></button>
            </div>
          )}
        </div>
      </header>

      <main className="p-6 max-w-2xl mx-auto">
        {/* Web-push infra (2026-09-17): manual push opt-in entry */}
        <PushEnableCard />
        {pendingEmailMessage && <div role="status" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{pendingEmailMessage}</div>}
        {/* Avatar Section */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-8 mb-6 text-center">
          <div className="relative inline-block">
            <div className="w-32 h-32 bg-[var(--v-teal-400)]/10 rounded-full flex items-center justify-center mx-auto overflow-hidden border-4 border-[var(--v-surface-1)] shadow-lg">
              {tempProfile.avatar ? (
                <img src={tempProfile.avatar} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-16 h-16 text-[var(--v-teal-400)]" />
              )}
            </div>
            {isEditing && (
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-10 h-10 bg-[var(--v-teal-400)] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[var(--v-teal-500)]"
              >
                <Camera className="w-5 h-5" />
              </button>
            )}
            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={handleImageUpload}
              disabled={avatarBusy}
            />
          </div>
          {/* FEATURE (2026-09-01, Jaime): avatar upload state + remove option. */}
          <div className="mt-3 flex flex-col items-center gap-2">
            {avatarBusy && <p className="text-xs text-[var(--v-text-secondary)]" role="status">Subiendo foto…</p>}
            {avatarError && <p className="text-xs text-[var(--v-danger)]" role="alert">{avatarError}</p>}
            {isEditing && tempProfile.avatar && (
              <button type="button" onClick={handleRemoveAvatar} disabled={avatarBusy} className="text-sm font-semibold text-[var(--v-danger)] hover:underline disabled:opacity-50">
                Quitar foto
              </button>
            )}
          </div>
          
          {isEditing ? (
            <input 
              type="text" 
              value={tempProfile.name}
              onChange={(e) => setTempProfile({...tempProfile, name: e.target.value})}
              className="mt-4 text-xl font-bold text-[var(--v-text-primary)] text-center w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl"
            />
          ) : (
            <h2 className="mt-4 text-2xl font-bold text-[var(--v-text-primary)]">{profile.name}</h2>
          )}
          <p className="text-[var(--v-text-secondary)]">Residente</p>
        </div>

        {/* P1-C (2026-09-08): profile completeness meter over {avatar, phone, bio, ICE, date_of_birth, sex}. */}
        {(() => {
          const items = [
            { label: 'Foto', done: !!profile.avatar },
            { label: 'Teléfono', done: !!profile.phone },
            { label: 'Biografía', done: !!profile.bio },
            { label: 'Contacto de emergencia', done: !!profile.iceContactName && !!profile.iceContactPhone },
            { label: 'Fecha de nacimiento', done: !!profile.dateOfBirth },
            { label: 'Sexo', done: !!profile.sex },
          ];
          const pct = Math.round((items.filter(i => i.done).length / items.length) * 100);
          return (
            <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-[var(--v-text-primary)]">Perfil completo</h3>
                <span className="text-sm font-semibold text-[var(--v-teal-400)]">{pct}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-[var(--v-surface-3)] overflow-hidden">
                <div className="h-full rounded-full bg-[var(--v-teal-400)] transition-all" style={{ width: pct + '%' }} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {items.map(i => (
                  <span key={i.label} className={'text-xs px-2 py-1 rounded-full ' + (i.done ? 'bg-[var(--v-teal-400)]/15 text-[var(--v-teal-400)]' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]')}>
                    {i.done ? 'Listo:' : 'Falta:'} {i.label}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Personal Info */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
          <h3 className="font-bold text-white mb-4">Información Personal</h3>
          {/* (2026-09-08) Inline error banner for save failures (e.g. 400 validation on date_of_birth). */}
          {profileSaveError && (
            <div className="mb-4 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" role="alert">
              {profileSaveError}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-1"><Mail className="w-4 h-4" /> Email</label>
              {isEditing ? (
                <input type="email" value={tempProfile.email} onChange={(e) => setTempProfile({...tempProfile, email: e.target.value})} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]" />
              ) : (
                <p className="text-[var(--v-text-primary)]">{profile.email}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-1"><Phone className="w-4 h-4" /> Teléfono</label>
              {isEditing ? (
                <input type="tel" value={tempProfile.phone} onChange={(e) => setTempProfile({...tempProfile, phone: e.target.value})} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]" />
              ) : (
                <p className="text-[var(--v-text-primary)]">{profile.phone || 'No registrado'}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm text-[var(--v-text-secondary)] mb-1">Biografía</label>
              {isEditing ? (
                <textarea 
                  value={tempProfile.bio} 
                  onChange={(e) => setTempProfile({...tempProfile, bio: e.target.value})}
                  className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl h-24 resize-none text-[var(--v-text-primary)]"
                />
              ) : (
                <p className="text-[var(--v-text-primary)]">{profile.bio || 'Sin biografía'}</p>
              )}
            </div>

            {/* P1-C (2026-09-08): emergency (ICE) contact */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-[var(--v-border-light)]">
              <div>
                <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" /> Contacto de emergencia (nombre)</label>
                {isEditing ? (
                  <input type="text" value={tempProfile.iceContactName} onChange={(e) => setTempProfile({ ...tempProfile, iceContactName: e.target.value })} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]" />
                ) : (
                  <p className="text-[var(--v-text-primary)]">{profile.iceContactName || 'No registrado'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-1"><Phone className="w-4 h-4" /> Teléfono de emergencia</label>
                {isEditing ? (
                  <input type="tel" value={tempProfile.iceContactPhone} onChange={(e) => setTempProfile({ ...tempProfile, iceContactPhone: e.target.value })} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]" />
                ) : (
                  <p className="text-[var(--v-text-primary)]">{profile.iceContactPhone || 'No registrado'}</p>
                )}
              </div>
            </div>

            {/* P1-C: directory visibility (default OFF) */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-[var(--v-surface-2)]">
              <div className="mr-3">
                <p className="text-sm font-medium text-[var(--v-text-primary)]">Visible en el directorio de vecinos</p>
                <p className="text-xs text-[var(--v-text-secondary)]">Desactivado por defecto. Si lo activas, tus datos básicos aparecerán en el directorio.</p>
              </div>
              {isEditing ? (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={tempProfile.directoryVisible} onChange={(e) => setTempProfile({ ...tempProfile, directoryVisible: e.target.checked })} className="sr-only peer" />
                  <div className="w-11 h-6 bg-[var(--v-surface-3)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--v-border-strong)] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--v-teal-400)]"></div>
                </label>
              ) : (
                <span className="text-sm text-[var(--v-text-secondary)]">{profile.directoryVisible ? 'Visible' : 'Oculto'}</span>
              )}
            </div>

            {/* Demographics (2026-09-08): optional stats, PATCH /auth/me (sex / age_range) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-[var(--v-border-light)]">
              <div>
                <label className="text-sm text-[var(--v-text-secondary)] mb-1">Sexo</label>
                {isEditing ? (
                  <select value={tempProfile.sex} onChange={(e) => setTempProfile({ ...tempProfile, sex: e.target.value })} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]">
                    <option value="">—</option>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                    <option value="otro">Otro</option>
                    <option value="prefiero_no_decir">Prefiero no decir</option>
                  </select>
                ) : (
                  <p className="text-[var(--v-text-primary)]">{(profile.sex && SEX_LABELS[profile.sex]) || 'No registrado'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-[var(--v-text-secondary)] mb-1">Rango de edad</label>
                {isEditing ? (
                  <select value={tempProfile.ageRange} onChange={(e) => setTempProfile({ ...tempProfile, ageRange: e.target.value })} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]">
                    <option value="">—</option>
                    <option value="<18">Menos de 18</option>
                    <option value="18-24">18–24</option>
                    <option value="25-34">25–34</option>
                    <option value="35-44">35–44</option>
                    <option value="45-54">45–54</option>
                    <option value="55-64">55–64</option>
                    <option value="65+">65 o más</option>
                    <option value="prefiero_no_decir">Prefiero no decir</option>
                  </select>
                ) : (
                  <p className="text-[var(--v-text-primary)]">{(profile.ageRange && AGE_RANGE_LABELS[profile.ageRange]) || 'No registrado'}</p>
                )}
              </div>
              {/* (2026-09-08) Fecha de nacimiento: optional date input -> date_of_birth (snake_case), cleared sends null.
                  min/max mirror the backend validation (year >= 1900, not in the future). */}
              <div className="sm:col-span-2">
                <label className="text-sm text-[var(--v-text-secondary)] mb-1">Fecha de nacimiento <span className="text-[var(--v-text-tertiary)]">(opcional)</span></label>
                {isEditing ? (
                  <input
                    type="date"
                    min="1900-01-01"
                    max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)}
                    value={tempProfile.dateOfBirth}
                    onChange={(e) => setTempProfile({ ...tempProfile, dateOfBirth: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]"
                  />
                ) : (
                  <p className="text-[var(--v-text-primary)]">{profile.dateOfBirth || 'No registrada'}</p>
                )}
              </div>
            </div>
            {/* P1-C: language preference (preferredLanguage on PATCH /auth/me) */}
            <div>
              <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-1"><MapPin className="w-4 h-4" /> Idioma</label>
              {isEditing ? (
                <select value={tempProfile.language} onChange={(e) => { setTempProfile({ ...tempProfile, language: e.target.value }); void handleLanguageChange(e.target.value); }} className="w-full px-4 py-2 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl text-[var(--v-text-primary)]">
                  <option value="es-MX">Español (México)</option>
                  <option value="es-VE">Español (Venezuela)</option>
                  <option value="es-ES">Español (España)</option>
                  <option value="en">English</option>
                </select>
              ) : (
                <p className="text-[var(--v-text-primary)]">{LANGUAGE_LABELS[profile.language as keyof typeof LANGUAGE_LABELS] || 'Español (México)'}</p>
              )}
            </div>
            {/* P1-C (2026-09-08): Unidad is NOT an editable field. Read-only
                membership context cards from /auth/me (role · unit · condo).
                Membership editing happens only via the admin/invitation flow. */}
            <div>
              <label className="text-sm text-[var(--v-text-secondary)] flex items-center gap-1 mb-2"><Home className="w-4 h-4" /> Membresías (solo lectura)</label>
              {(memberships && memberships.length > 0 ? memberships : []).map((m, i) => (
                <div key={m.condoId || i} className="mb-2 p-3 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)]">
                  <p className="font-semibold text-[var(--v-text-primary)]">{m.condoName || 'Condominio'}</p>
                  <p className="text-sm text-[var(--v-text-secondary)]">
                    {m.role || 'miembro'} · {m.unitLabel || m.unitNumber || 'sin unidad asignada'}
                  </p>
                </div>
              ))}
              {(!memberships || memberships.length === 0) && <p className="text-[var(--v-text-primary)]">{profile.address}</p>}
            </div>


          </div>
        </div>

        {/* Mis Propiedades */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
          <h3 className="font-bold text-[var(--v-text-primary)] mb-2 flex items-center gap-2"><Building2 className="w-5 h-5 text-[var(--v-teal-400)]" /> Mis Propiedades</h3>
          <p className="text-sm text-[var(--v-text-secondary)] mb-2">Aquí puedes administrar tus propiedades y sus accesos.</p>
          <div className="mb-4 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)] p-3 text-xs text-[var(--v-text-secondary)] space-y-1">
            <p><strong className="text-[var(--v-text-primary)]">Unirme a una unidad de condominio:</strong> usa el código de la dirección del condominio (te lo da la administración o el propietario).</p>
            <p><strong className="text-[var(--v-text-primary)]">Vincular tu propiedad a un condominio:</strong> si tu propiedad es independiente, usa "Vincular a condominio" en esa propiedad con el código de la dirección.</p>
            <p><strong className="text-[var(--v-text-primary)]">Compartir tu hogar:</strong> cada propiedad tiene su propio código para invitar a familiares o inquilinos que vivirán contigo.</p>
          </div>
          {incomingTransfers.map(t => <div key={t.id} className="mb-4 p-4 rounded-xl border border-[var(--v-teal-400)]/40 bg-[var(--v-teal-400)]/10"><p className="font-semibold text-[var(--v-text-primary)]">{t.to_email} quiere transferirte {t.household_name || `la unidad ${t.unit_number || ''}`} {t.condo_name ? `en ${t.condo_name}` : ''}</p><div className="flex gap-2 mt-3"><button disabled={propertyBusy} onClick={() => respondTransfer(t.id, 'accept')} className="px-3 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold">Aceptar</button><button disabled={propertyBusy} onClick={() => respondTransfer(t.id, 'decline')} className="px-3 py-2 rounded-lg bg-[var(--v-surface-2)] text-[var(--v-text-primary)] text-sm font-semibold">Rechazar</button></div></div>)}
          {outgoingTransfers.map(t => <div key={`out-${t.id}`} className="mb-4 p-4 rounded-xl border border-amber-300/50 bg-amber-50/50 dark:bg-amber-900/10"><p className="font-semibold text-[var(--v-text-primary)]">Transferencia pendiente a {t.to_email}</p><button disabled={propertyBusy} onClick={() => cancelTransfer(t.id)} className="mt-3 px-3 py-2 rounded-lg bg-[var(--v-surface-2)] text-[var(--v-text-primary)] text-sm font-semibold">Cancelar</button></div>)}
          {claimCandidatesLoading && <p className="mb-3 text-sm text-[var(--v-text-secondary)]">Buscando unidades disponibles…</p>}
          {claimCandidates.length > 0 && <div className="mb-4 rounded-xl border border-[var(--v-teal-400)]/40 bg-[var(--v-teal-400)]/10 p-4"><p className="font-semibold text-[var(--v-text-primary)]">Reclamar propiedad</p><p className="mt-1 mb-3 text-sm text-[var(--v-text-secondary)]">Eres miembro de estos condominios. Si una unidad es tuya, asígnale un nombre y reclámala como propietario.</p><div className="space-y-3">{claimCandidates.map(candidate => <div key={candidate.id} className="rounded-lg bg-[var(--v-surface-1)] p-3"><p className="text-sm font-semibold text-[var(--v-text-primary)]">{candidate.condoName} · Unidad {candidate.unitNumber || 'sin número'}</p><div className="mt-2 flex gap-2"><input value={candidate.name} onChange={e => setClaimCandidates(items => items.map(item => item.id === candidate.id ? { ...item, name: e.target.value } : item))} placeholder="Nombre: Las Hamacas, TH1…" className="min-w-0 flex-1 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] px-3 py-2 text-[var(--v-text-primary)]" /><button disabled={claimingUnit} onClick={() => claimMembershipUnit(candidate)} className="rounded-lg bg-[var(--v-teal-400)] px-3 py-2 text-sm font-semibold text-white">Reclamar</button></div></div>)}</div></div>}
          <div className="space-y-3">
            {mergedUnits.length === 0 && <div className="mb-4 rounded-xl bg-[var(--v-surface-2)] p-4 text-sm text-[var(--v-text-secondary)]"><p>Aún no tienes propiedades. Crea una (ej. tu casa) o únete a una unidad de un condominio con su código.</p></div>}
            {mergedUnits.map(unit => unit.isMembershipOnly ? <div key={unit.id} className="p-4 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)]" data-testid="membership-unit-card"><div className="flex justify-between gap-3"><div className="min-w-0 flex-1"><p className="font-semibold text-[var(--v-text-primary)]">{unit.household_name || (unit.unit_number ? `Unidad ${unit.unit_number}` : 'Mi unidad')}</p><p className="text-sm text-[var(--v-text-secondary)]">Unidad {unit.unit_number || 'Sin número'} · {unit.condo_name || 'Sin vincular'}</p><span className="mt-1 inline-block rounded-full bg-[var(--v-surface-3)] px-2 py-0.5 text-xs text-[var(--v-text-secondary)]">{roleLabel(unit.role)}</span></div><Home className="w-5 h-5 text-[var(--v-teal-400)]" /></div><button type="button" onClick={() => { setLeaveTarget(unit); setPropertyError(''); }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--v-border-light)] px-3 py-2 text-sm font-semibold text-[var(--v-text-secondary)]">Desvincularme</button></div> : <div key={unit.id} className="p-4 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)]"><div className="flex justify-between gap-3"><div className="min-w-0 flex-1">{editingUnitId === unit.id ? <div className="flex gap-2"><input autoFocus value={editingUnitName} onChange={e => setEditingUnitName(e.target.value)} className="min-w-0 flex-1 px-2 py-1 rounded border bg-[var(--v-surface-1)] text-[var(--v-text-primary)]" /><button disabled={propertyBusy} onClick={() => renameUnit(unit.id)} className="text-emerald-500" aria-label="Guardar nombre"><Check className="w-4 h-4" /></button><button onClick={() => setEditingUnitId(null)} className="text-[var(--v-danger)]" aria-label="Cancelar"><X className="w-4 h-4" /></button></div> : <div className="flex items-center gap-2"><p className="font-semibold text-[var(--v-text-primary)]">{unit.household_name || 'Mi propiedad'}</p><button onClick={() => { setEditingUnitId(unit.id); setEditingUnitName(unit.household_name || '') }} aria-label="Renombrar propiedad" className="text-[var(--v-teal-400)]"><Edit2 className="w-4 h-4" /></button></div>}<p className="text-sm text-[var(--v-text-secondary)]">Unidad {unit.unit_number || 'Sin número'} · {unit.condo_name || 'Sin vincular'}</p></div><Home className="w-5 h-5 text-[var(--v-teal-400)]" /></div>{unit.invite_code && <div className="mt-3 flex items-center gap-2"><div className="flex-1 min-w-0"><p className="mb-1 text-xs font-semibold text-[var(--v-text-secondary)]">Tu código de unidad</p><code className="block px-3 py-2 rounded-lg bg-[var(--v-surface-1)] text-[var(--v-text-primary)] font-mono text-sm">{unit.invite_code}</code></div><button title="Copiar código" onClick={() => copyCode(unit)} className="p-2 rounded-lg bg-[var(--v-teal-400)] text-white"><Copy className="w-4 h-4" /></button>{copiedUnit === unit.id && <span className="text-xs text-[var(--v-teal-400)]">¡Copiado!</span>}</div>}<p className="text-xs text-[var(--v-text-tertiary)] mt-2 flex items-center gap-1"><Share2 className="w-3 h-3" /> Comparte este código solo con personas de confianza.</p>{!unit.condo_id && <div className="mt-3"><div className="flex flex-wrap items-center gap-x-4 gap-y-2"><button onClick={() => { setSelectedUnitId(unit.id); setShowCondoRegistration(null); setPropertyError(''); }} className="text-sm font-semibold text-[var(--v-teal-400)]">Vincular a condominio</button><button type="button" onClick={() => { setShowCondoRegistration(unit.id); setSelectedUnitId(null); setPropertyError(''); }} className="text-sm font-semibold text-[var(--v-teal-400)]">Registrar un nuevo condominio</button></div>{selectedUnitId === unit.id && <div className="mt-2"><label className="mb-1 block text-xs font-semibold text-[var(--v-text-secondary)]">Código de tu dirección en el condominio (te lo da la administración)</label><div className="flex gap-2"><input value={linkCode} onChange={e => setLinkCode(e.target.value)} placeholder="Código de tu dirección en el condominio" className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-1)] text-[var(--v-text-primary)]" /><button disabled={propertyBusy} onClick={linkProperty} className="px-3 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm">Vincular</button></div></div>}{showCondoRegistration === unit.id && <div className="mt-3 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-4"><button type="button" onClick={() => setShowCondoRegistration(null)} className="mb-2 text-sm font-semibold text-[var(--v-text-secondary)]">Cerrar</button><CondoOnboarding activeUnitId={unit.id} onRequested={(id) => { setSelectedUnitId(id || unit.id); setShowCondoRegistration(null); setPropertySuccess('Solicitud enviada. Cuando recibas el código de la dirección, quedará listo para vincularlo aquí.'); }} onLinked={() => { setShowCondoRegistration(null); refreshMe().catch(() => undefined); loadProperties(); }} /></div>}</div>}<details className="mt-3"><summary className="cursor-pointer text-sm text-[var(--v-text-secondary)] flex items-center gap-1"><UserPlus className="w-4 h-4" /> Transferir propiedad</summary><p className="mt-1 text-xs text-[var(--v-text-tertiary)]">Transfiere la propiedad y su membresía del condominio a otro usuario por correo.</p><div className="flex gap-2 mt-2"><input value={transferEmail} onChange={e => setTransferEmail(e.target.value)} type="email" placeholder="correo@ejemplo.com" className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-1)] text-[var(--v-text-primary)]" /><button disabled={propertyBusy} onClick={() => transferProperty(unit.id)} className="px-3 py-2 rounded-lg bg-[var(--v-surface-3)] text-[var(--v-text-primary)] text-sm">Enviar</button></div></details>{!unit.condo_id && <button type="button" onClick={() => { setDeleteTarget(unit); setDeleteConfirmText(''); setPropertyError(''); }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--v-danger)] px-3 py-2 text-sm font-semibold text-[var(--v-danger)]">🗑️ Eliminar propiedad</button>}{unit.condo_id && (unit.is_owner === false || (unit.role && !['owner', 'propietario'].includes(String(unit.role).toLowerCase()))) && <button type="button" onClick={() => { setLeaveTarget(unit); setPropertyError(''); }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--v-border-light)] px-3 py-2 text-sm font-semibold text-[var(--v-text-secondary)]">Desvincularme</button>}</div>)}
            <div className="p-4 rounded-xl border border-dashed border-[var(--v-border-strong)]">
              <p className="font-semibold text-[var(--v-text-primary)] mb-2">Agregar propiedad</p>
              {!showCreateUnit ? (
                <button disabled={propertyBusy} onClick={() => { setShowCreateUnit(true); setPropertyError(''); }} className={`px-3 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold ${highlightAgregar ? "ring-4 ring-[var(--v-teal-400)]/40 ring-offset-2 ring-offset-[var(--v-surface-1)] animate-pulse" : ""}`}><Plus className="w-4 h-4 inline" /> Agregar propiedad</button>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-[var(--v-text-secondary)]">Igual que en el registro: se requiere el país y la dirección completa para crear una propiedad independiente.</p>
                  <input value={newUnit.householdName} onChange={updateNewUnit('householdName')} placeholder="Nombre (opcional): Ej. Casa de playa" className="w-full px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={newUnit.street} onChange={updateNewUnit('street')} placeholder="Calle *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                    <input value={newUnit.number} onChange={updateNewUnit('number')} placeholder="Número *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                    <input value={newUnit.neighborhood} onChange={updateNewUnit('neighborhood')} placeholder="Colonia *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                    <input value={newUnit.locality} onChange={updateNewUnit('locality')} placeholder="Localidad *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                    <input value={newUnit.postal_code} onChange={updateNewUnit('postal_code')} placeholder="Código postal *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                    <input value={newUnit.country} onChange={updateNewUnit('country')} placeholder="País *" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={newUnit.city} onChange={updateNewUnit('city')} placeholder="Ciudad (opcional)" className="min-w-0 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] text-[var(--v-text-primary)]" />
                  </div>
                  <MapLocationPicker
                    latitude={newUnit.latitude}
                    longitude={newUnit.longitude}
                    disabled={propertyBusy}
                    onPick={(latitude, longitude) => setNewUnit((prev) => ({ ...prev, latitude, longitude }))}
                    onAddress={(address: MapPickedAddress) => setNewUnit((prev) => ({
                      ...prev,
                      street: address.street ?? prev.street,
                      number: address.number ?? prev.number,
                      neighborhood: address.neighborhood ?? prev.neighborhood,
                      locality: address.locality ?? prev.locality,
                      postal_code: address.postal_code ?? prev.postal_code,
                      country: address.country ?? prev.country,
                      city: address.city ?? prev.city,
                    }))}
                  />
                  <div className="flex gap-2">
                    <button disabled={propertyBusy} onClick={createProperty} className="px-3 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold"><Plus className="w-4 h-4 inline" /> Crear propiedad</button>
                    <button disabled={propertyBusy} onClick={() => { setShowCreateUnit(false); setNewUnit(emptyNewUnit); setPropertyError(''); }} className="px-3 py-2 rounded-lg bg-[var(--v-surface-3)] text-[var(--v-text-primary)] text-sm font-semibold">Cancelar</button>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 rounded-xl bg-[var(--v-surface-2)]"><p className="font-semibold text-[var(--v-text-primary)] mb-1"><KeyRound className="w-4 h-4 inline text-[var(--v-teal-400)]" /> Unirme a una unidad de condominio</p><p className="text-xs text-[var(--v-text-secondary)] mb-2">Si vas a rentar o vivir en una unidad de un condominio, pide el código de ESA dirección al propietario o administración.</p><div className="flex gap-2"><input value={joinCode} onChange={e => setJoinCode(e.target.value)} placeholder="Código de la dirección en el condominio" className="min-w-0 flex-1 px-3 py-2 rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-1)] text-[var(--v-text-primary)]" /><button disabled={propertyBusy} onClick={joinWithUnitCode} className="px-3 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm font-semibold">Unirme</button></div></div>
            {deleteTarget && (
              <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label="Eliminar propiedad">
                <div className="w-full max-w-md rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-5">
                  <h3 className="flex items-center gap-2 text-lg font-bold text-[var(--v-text-primary)]"><AlertTriangle className="w-5 h-5 text-[var(--v-danger)]" /> Eliminar propiedad</h3>
                  <p className="mt-2 text-sm text-[var(--v-text-secondary)]">Vas a eliminar <span className="font-semibold">{deleteTarget.household_name || 'Mi propiedad'}</span>{deleteTarget.unit_number ? ` (Unidad ${deleteTarget.unit_number})` : ''}. Esta acción es <strong>irreversible</strong>.</p>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[var(--v-text-secondary)]">
                    <li>Todos los residentes vinculados perderán el acceso de inmediato.</li>
                    <li>La suscripción Home+ activa de esta unidad será cancelada.</li>
                  </ul>
                  <label htmlFor="delete-confirm-input" className="mt-4 block text-xs font-semibold text-[var(--v-text-secondary)]">Escribe <span className="font-mono font-bold">eliminar</span> para confirmar</label>
                  <input id="delete-confirm-input" autoFocus value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} className="mt-1 w-full rounded-lg border border-[var(--v-border-light)] bg-[var(--v-surface-2)] px-3 py-2 text-[var(--v-text-primary)]" placeholder="eliminar" />
                  {propertyError && <p role="alert" className="mt-2 text-sm text-[var(--v-danger)]">{propertyError}</p>}
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--v-text-secondary)]">Cancelar</button>
                    <button type="button" disabled={propertyBusy || deleteConfirmText.trim().toLowerCase() !== 'eliminar'} onClick={deleteProperty} className="rounded-lg bg-[var(--v-danger)] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Eliminar definitivamente</button>
                  </div>
                </div>
              </div>
            )}
            {leaveTarget && (
              <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center" role="dialog" aria-modal="true" aria-label="Desvincularme de la unidad">
                <div className="w-full max-w-md rounded-2xl border border-[var(--v-border-light)] bg-[var(--v-surface-1)] p-5">
                  <h3 className="text-lg font-bold text-[var(--v-text-primary)]">Desvincularme de la unidad</h3>
                  <p className="mt-2 text-sm text-[var(--v-text-secondary)]">¿Seguro que quieres dejar <span className="font-semibold">{leaveTarget.household_name || `la unidad ${leaveTarget.unit_number || ''}`}</span>? Perderás el acceso a esta unidad y su condominio.</p>
                  {propertyError && <p role="alert" className="mt-2 text-sm text-[var(--v-danger)]">{propertyError}</p>}
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={() => setLeaveTarget(null)} className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--v-text-secondary)]">Cancelar</button>
                    <button type="button" disabled={propertyBusy} onClick={leaveUnit} className="rounded-lg bg-[var(--v-danger)] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">Sí, desvincularme</button>
                  </div>
                </div>
              </div>
            )}
            {propertyError && <p role="alert" className="text-sm text-[var(--v-danger)]">{propertyError}</p>}
            {propertySuccess && <p role="status" className="text-sm text-[var(--v-teal-400)]">{propertySuccess}</p>}
          </div>
        </div>

        {/* F18 (packet 2026-09-24): per-condo directory visibility opt-out. */}
        <DirectoryPrivacyCard />

        {/* Security */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-[var(--v-teal-400)]" /> Seguridad</h3>
          
          <div className="space-y-4">
            <button onClick={handleOpenPasswordModal} className="w-full flex items-center justify-between p-4 bg-[var(--v-surface-2)] rounded-xl hover:bg-[var(--v-surface-3)] transition-colors">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-[var(--v-text-tertiary)]" />
                <span className="text-[var(--v-text-primary)] font-medium">Cambiar Contraseña</span>
              </div>
              <ArrowLeft className="w-5 h-5 text-[var(--v-text-tertiary)] rotate-180" />
            </button>

            <div className="flex items-center justify-between p-4 bg-[var(--v-surface-2)] rounded-xl">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-[var(--v-text-tertiary)]" />
                <span className="text-[var(--v-text-primary)] font-medium">Notificaciones</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={profile.notifications} className="sr-only peer" />
                <div className="w-11 h-6 bg-[var(--v-surface-3)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[var(--v-border-strong)] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--v-teal-400)]"></div>
              </label>
            </div>

            {/* P1-C (2026-09-08, spec ADDENDUM): "Preferencias por categoría" REMOVED.
                User-scoped notification prefs remain server-managed (P1-A contract). */}

            <div className="p-4 bg-[var(--v-surface-2)] rounded-xl space-y-4">
              <div className="flex items-center justify-between"><div className="flex items-center gap-3"><Shield className="w-5 h-5 text-[var(--v-text-tertiary)]" /><div><span className="text-[var(--v-text-primary)] font-medium block">Autenticación de dos pasos</span><span className="text-sm text-[var(--v-text-secondary)]">{twoFactorEnabled ? 'Activado' : 'Desactivado'}</span></div></div>{twoFactorStep === 'idle' && <button onClick={twoFactorEnabled ? () => setTwoFactorStep('disable') : start2FA} className="px-4 py-2 rounded-lg bg-[var(--v-teal-400)] text-white text-sm">{twoFactorEnabled ? 'Desactivar' : 'Activar'}</button>}</div>
              {twoFactorStep === 'enroll' && <div className="space-y-3"><p className="text-sm text-[var(--v-text-secondary)]">Escanea el QR con tu aplicación autenticadora.</p>{twoFactorUrl && <img src={`https://quickchart.io/qr?text=${encodeURIComponent(twoFactorUrl)}&size=180`} alt="Código QR" className="w-44 h-44 bg-white p-2 rounded-lg" />}<p className="text-xs break-all">Clave manual: <strong>{twoFactorSecret}</strong></p><input inputMode="numeric" maxLength={6} value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))} placeholder="Código de 6 dígitos" className="w-full px-3 py-2 rounded-lg border bg-[var(--v-surface-1)]" /><button onClick={confirm2FA} className="px-4 py-2 rounded-lg bg-[var(--v-teal-400)] text-white">Confirmar activación</button></div>}
              {twoFactorStep === 'disable' && <div className="space-y-3"><p className="text-sm">Escribe un código para desactivar.</p><input value={twoFactorCode} onChange={e => setTwoFactorCode(e.target.value)} placeholder="Código" className="w-full px-3 py-2 rounded-lg border bg-[var(--v-surface-1)]" /><button onClick={disable2FA} className="px-4 py-2 rounded-lg bg-red-500 text-white">Confirmar</button></div>}
              {twoFactorStep === 'backup' && <div className="space-y-3"><p className="font-semibold">Guarda tus códigos de respaldo (solo se mostrarán una vez).</p><div className="grid grid-cols-2 gap-2 font-mono text-sm">{backupCodes.map(c => <span key={c} className="p-2 bg-[var(--v-surface-1)] rounded">{c}</span>)}</div><button onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))} className="px-4 py-2 rounded-lg border">Copiar códigos</button><button onClick={() => { setBackupCodes([]); setTwoFactorStep('idle'); }} className="ml-2 px-4 py-2 rounded-lg bg-[var(--v-teal-400)] text-white">Los guardé</button></div>}
              {twoFactorError && <p className="text-sm text-red-500">{twoFactorError}</p>}
            </div>
          </div>
        </div>

        {/* Sign out — reuses AuthContext.logout (token revocation + clearAuth + redirect to /login) */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-6 mb-6">
          <button
            onClick={() => { logout(); }}
            className="w-full px-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-[var(--v-danger)] bg-[var(--v-danger-bg)] hover:bg-[var(--v-danger)] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>

        {/* Zona de Peligro — Account Deletion */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border-2 border-[var(--v-danger)]/20 shadow-sm p-6 mb-6">
          <h3 className="font-bold text-[var(--v-danger)] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Zona de Peligro
          </h3>
          
          <div className="p-4 bg-[var(--v-danger-bg)] rounded-xl">
            <div className="flex items-start gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-[var(--v-danger)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-[var(--v-text-primary)]">Eliminar cuenta</p>
                <p className="text-sm text-[var(--v-text-secondary)] mt-1">
                  Al eliminar tu cuenta, se borrarán permanentemente todos tus datos personales:
                  tareas, compras, mascotas, vehículos, presupuestos, notificaciones, códigos de acceso
                  y alertas. Esta acción <strong>no se puede deshacer</strong>.
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-full px-4 py-3 bg-[var(--v-danger)] text-white font-bold rounded-xl hover:opacity-90 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Eliminar cuenta
            </button>
          </div>
        </div>
      </main>

      {/* F101: Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[var(--v-teal-400)]/10 rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-[var(--v-teal-400)]" />
              </div>
              <h2 className="text-xl font-bold text-[var(--v-text-primary)]">Cambiar Contraseña</h2>
            </div>

            {pwSuccess ? (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
                <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-600 font-medium">{pwSuccess}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current password */}
                <div>
                  <label className="text-sm font-medium text-[var(--v-text-primary)] flex items-center gap-1 mb-2">
                    <Lock className="w-4 h-4" /> Contraseña actual
                  </label>
                  <div className="relative">
                    <input
                      type={showPwCurrent ? 'text' : 'password'}
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                      placeholder="Tu contraseña actual"
                      className="w-full px-4 py-3 pr-11 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:outline-none focus:border-[var(--v-teal-400)] text-[var(--v-text-primary)]"
                      disabled={pwLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwCurrent(!showPwCurrent)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)]"
                    >
                      {showPwCurrent ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* New password */}
                <div>
                  <label className="text-sm font-medium text-[var(--v-text-primary)] flex items-center gap-1 mb-2">
                    <Lock className="w-4 h-4" /> Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPwNew ? 'text' : 'password'}
                      value={pwNew}
                      onChange={(e) => setPwNew(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      className="w-full px-4 py-3 pr-11 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:outline-none focus:border-[var(--v-teal-400)] text-[var(--v-text-primary)]"
                      disabled={pwLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwNew(!showPwNew)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-primary)]"
                    >
                      {showPwNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {pwNew && pwNew.length < 8 && (
                    <p className="text-xs text-[var(--v-danger)] mt-1">La contraseña debe tener al menos 8 caracteres</p>
                  )}
                </div>

                {/* Confirm new password */}
                <div>
                  <label className="text-sm font-medium text-[var(--v-text-primary)] flex items-center gap-1 mb-2">
                    <Lock className="w-4 h-4" /> Confirmar nueva contraseña
                  </label>
                  <input
                    type="password"
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    placeholder="Repite la nueva contraseña"
                    className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:outline-none focus:border-[var(--v-teal-400)] text-[var(--v-text-primary)]"
                    disabled={pwLoading}
                  />
                  {pwConfirm && pwNew !== pwConfirm && (
                    <p className="text-xs text-[var(--v-danger)] mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>

                {pwError && (
                  <div className="p-3 bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/20 rounded-xl text-sm text-[var(--v-danger)]">
                    {pwError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closePasswordModal}
                    disabled={pwLoading}
                    className="flex-1 px-4 py-3 bg-[var(--v-surface-2)] text-[var(--v-text-primary)] font-semibold rounded-xl hover:bg-[var(--v-surface-3)] transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={pwLoading || !pwCurrent || !pwNew || !pwConfirm || pwNew.length < 8 || pwNew !== pwConfirm}
                    className="flex-1 px-4 py-3 bg-[var(--v-teal-400)] text-white font-bold rounded-xl hover:bg-[var(--v-teal-500)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {pwLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Cambiando...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4" /> Cambiar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Account Deletion Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[var(--v-danger-bg)] rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[var(--v-danger)]" />
              </div>
              <h2 className="text-xl font-bold text-[var(--v-text-primary)]">Eliminar mi cuenta</h2>
            </div>

            <p className="text-[var(--v-text-secondary)] mb-4">
              Esta acción es <strong>permanente e irreversible</strong>. Se eliminarán todos tus datos
              personales asociados a tu cuenta en Vecindus.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[var(--v-text-primary)] flex items-center gap-1 mb-2">
                  <Lock className="w-4 h-4" /> Confirma tu contraseña
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Tu contraseña actual"
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:outline-none focus:border-[var(--v-danger)] text-[var(--v-text-primary)]"
                  disabled={deleting}
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-[var(--v-border-strong)] text-[var(--v-danger)] focus:ring-[var(--v-danger)]"
                  disabled={deleting}
                />
                <span className="text-sm text-[var(--v-text-primary)]">
                  Entiendo que esto es permanente y que todos mis datos serán eliminados.
                </span>
              </label>

              {deleteError && (
                <div className="p-3 bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/20 rounded-xl text-sm text-[var(--v-danger)]">
                  {deleteError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-[var(--v-surface-2)] text-[var(--v-text-primary)] font-semibold rounded-xl hover:bg-[var(--v-surface-3)] transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting || !deletePassword || !deleteConfirmed}
                  className="flex-1 px-4 py-3 bg-[var(--v-danger)] text-white font-bold rounded-xl hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" /> Eliminar mi cuenta
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
